import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Mail } from 'lucide-react';

/**
 * CandidateGuard — validates the `?room=` query param before mounting
 * CandidateView. If the room code is missing, empty, or whitespace-only,
 * the candidate sees a friendly, non-alarming error screen instead of
 * CandidateView's own "Room not found" state (which only fires after the
 * WebSocket rejects the id — that's a slower, uglier failure path).
 *
 * The check is intentionally strict about trimming and type so a malformed
 * QR / manual URL never leaks into the socket layer.
 */
export default function CandidateGuard({ children }) {
  const [searchParams] = useSearchParams();
  const raw = searchParams.get('room');
  const isValid = typeof raw === 'string' && raw.trim().length > 0;
  if (!isValid) return <CandidateRoomError />;
  return children;
}

/**
 * CandidateRoomError — full-screen Elite Dark placeholder. Not aterrador:
 * this is almost always "recruiter's QR was for another room", NOT the
 * candidate's fault.
 */
function CandidateRoomError() {
  return (
    <div
      className="candidate-room-error"
      data-testid="candidate-room-error"
      role="alert"
    >
      <div className="candidate-room-error-card">
        <div className="candidate-room-error-icon" aria-hidden="true">
          <AlertTriangle size={44} strokeWidth={2.2} />
        </div>
        <h1 className="candidate-room-error-title" data-testid="candidate-room-error-title">
          Invalid or Expired Room Link
        </h1>
        <p className="candidate-room-error-body">
          This interview link is missing a valid room code, or the room has been
          closed. Please ask the recruiter for a new link.
        </p>
        <div className="candidate-room-error-actions">
          <Link
            to="/"
            className="candidate-room-error-btn candidate-room-error-btn--primary"
            data-testid="candidate-room-error-home"
          >
            <ArrowLeft size={16} />
            <span>Back to Home</span>
          </Link>
          <a
            href="mailto:support@signvoice.app"
            className="candidate-room-error-btn candidate-room-error-btn--ghost"
            data-testid="candidate-room-error-support"
          >
            <Mail size={16} />
            <span>Contact Support</span>
          </a>
        </div>
      </div>
    </div>
  );
}
